import React, { useMemo, useState } from 'react';
import { AlertCircle, Bot, Link2, Loader2, Search, Video, WifiOff } from 'lucide-react';
import type { Meeting } from '../../types';
import type { AppUser, CalendarEventDTO } from '../../global';
import { Avatar } from '../../components/ui/Avatar';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useCalendar } from '../calendar/useCalendar';
import { useAutoJoin } from '../calendar/useAutoJoin';
import { BotStatusBadge } from '../bot/BotStatusBadge';
import { botStatusLabel } from '../bot/botStatus';
import { groupMeetings } from '../meetings/groupMeetings';
import { dateLocale, t } from '../../lib/i18n';
import { MobileCard, MobileSection, MobileTitle } from './MobileUI';

interface MobileHomeViewProps {
  user: AppUser | null;
  meetings: Meeting[];
  isLoading: boolean;
  error: string | null;
  onSelectMeeting: (meeting: Meeting) => void;
  /** Sağ üstteki profil fotoğrafı → Genel ayarlar. */
  onOpenProfile: () => void;
  onError: (message: string) => void;
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });

/** Arama: başlık, katılımcılar ve özet içinde, büyük/küçük harf ve Türkçe harflerden bağımsız. */
function matches(meeting: Meeting, query: string): boolean {
  const needle = query.toLocaleLowerCase(dateLocale());
  const haystack = [meeting.title, meeting.summary.tldr, ...meeting.participants.map((p) => p.name)]
    .join(' ')
    .toLocaleLowerCase(dateLocale());
  return haystack.includes(needle);
}

/** Telefonun ana sayfası: profil, sıradaki toplantı, arama ve toplantı listesi. */
export const MobileHomeView: React.FC<MobileHomeViewProps> = ({
  user,
  meetings,
  isLoading,
  error,
  onSelectMeeting,
  onOpenProfile,
  onError,
}) => {
  const isOnline = useOnlineStatus();
  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const results = useMemo(() => (trimmed ? meetings.filter((m) => matches(m, trimmed)) : []), [meetings, trimmed]);
  const { recent, past } = groupMeetings(meetings);

  const row = (meeting: Meeting, when: string) => (
    <button
      key={meeting.id}
      type="button"
      onClick={() => onSelectMeeting(meeting)}
      className="w-full min-h-[72px] flex items-center gap-3 pl-4 pr-3.5 py-3 text-left border-b border-border last:border-b-0 active:bg-secondary/60 transition-colors"
    >
      <span className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="text-[16px] font-semibold leading-[21px] text-foreground truncate">{meeting.title}</span>
        <span className="text-[14px] text-muted-foreground truncate">
          {when}
          {meeting.audioDurationSeconds > 0 ? ` · ${meeting.duration}` : ''}
          {meeting.participants.length > 1 ? ` · ${t(`${meeting.participants.length} kişi`, `${meeting.participants.length} people`)}` : ''}
        </span>
      </span>
      {meeting.bot ? (
        <BotStatusBadge bot={meeting.bot} />
      ) : meeting.status === 'in-progress' ? (
        <span className="shrink-0 text-[12px] font-semibold px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">
          {t('Sürüyor', 'In progress')}
        </span>
      ) : null}
    </button>
  );

  return (
    <div className="px-5 pb-8 space-y-[18px] select-none">
      <MobileTitle
        title={t('Toplantılar', 'Meetings')}
        trailing={
          <button
            type="button"
            onClick={onOpenProfile}
            aria-label={t('Profil ve ayarlar', 'Profile and settings')}
            data-tour="profile"
            className="rounded-full ring-2 ring-border ring-offset-2 ring-offset-background active:opacity-70"
          >
            <Avatar src={user?.avatarDataUrl} name={user?.name || user?.email || '?'} className="size-10 text-[14px]" />
          </button>
        }
      />

      {!isOnline && (
        <p className="flex items-center gap-2 text-[13.5px] font-medium text-record-text">
          <WifiOff className="w-4 h-4" />
          {t('Çevrimdışı — takvim ve AI kullanılamıyor', 'Offline — calendar and AI are unavailable')}
        </p>
      )}

      <MobileSection title={t('Sırada', 'Up next')}>
        <div data-tour="next-meeting">
          <NextMeetingCard onError={onError} />
        </div>
      </MobileSection>

      <label className="flex items-center gap-2.5 h-[46px] px-3.5 rounded-[14px] bg-secondary text-muted-foreground">
        <Search className="w-[18px] h-[18px] shrink-0" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('Toplantılarda ara', 'Search meetings')}
          aria-label={t('Toplantılarda ara', 'Search meetings')}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[16px] text-foreground placeholder:text-muted-foreground"
        />
      </label>

      {trimmed ? (
        <MobileSection title={t('Sonuçlar', 'Results')}>
          {results.length === 0 ? (
            <p className="py-4 text-center text-[15px] text-muted-foreground">
              {t('Eşleşen toplantı yok.', 'No matching meetings.')}
            </p>
          ) : (
            <MobileCard>{results.map((m) => row(m, `${m.date} ${m.time}`))}</MobileCard>
          )}
        </MobileSection>
      ) : (
        <>
          {isLoading && (
            <p className="py-6 flex items-center justify-center gap-2 text-[14px] text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('Toplantılar yükleniyor…', 'Loading meetings…')}
            </p>
          )}
          {error && (
            <p className="flex items-start gap-2 text-[14px] text-record-text">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </p>
          )}
          {!isLoading && !error && meetings.length === 0 && (
            <p className="py-4 text-center text-[15px] text-muted-foreground">
              {t('Henüz kayıtlı toplantı yok. Ortadaki düğmeyle ilk kaydını al.', 'No meetings yet. Use the button in the middle to record your first one.')}
            </p>
          )}

          {recent.map((group) => (
            <MobileSection key={group.key} title={group.heading}>
              <MobileCard>{group.meetings.map((m) => row(m, m.time))}</MobileCard>
            </MobileSection>
          ))}

          {past.length > 0 && (
            <MobileSection title={t('Geçmiş toplantılar', 'Past meetings')}>
              <MobileCard>{past.map((m) => row(m, m.date))}</MobileCard>
            </MobileSection>
          )}
        </>
      )}
    </div>
  );
};

/** Takvimdeki sıradaki toplantı: saat, bot durumu, Katıl ve bot düğmesi. */
const NextMeetingCard: React.FC<{ onError: (message: string) => void }> = ({ onError }) => {
  const calendar = useCalendar();
  const autoJoin = useAutoJoin();
  const [busy, setBusy] = useState(false);

  const next = useMemo<CalendarEventDTO | null>(() => {
    const now = Date.now();
    return (
      calendar.events
        .filter((event) => !event.isAllDay && new Date(event.endISO).getTime() > now)
        .sort((a, b) => a.startISO.localeCompare(b.startISO))[0] ?? null
    );
  }, [calendar.events]);

  if (calendar.status === 'idle' || calendar.status === 'not_connected' || calendar.status === 'auth_expired') {
    return (
      <MobileCard className="p-4 space-y-3">
        <p className="text-[15px] text-muted-foreground leading-[22px]">
          {calendar.status === 'auth_expired'
            ? t('Takvim bağlantısının süresi doldu.', 'Your calendar access has expired.')
            : t('Yaklaşan toplantılarını görmek ve botu otomatik göndermek için takvimini bağla.', 'Connect your calendar to see upcoming meetings and send the bot automatically.')}
        </p>
        <button
          type="button"
          onClick={() => void calendar.connect()}
          className="w-full h-11 rounded-xl bg-brand text-on-brand text-[15px] font-bold flex items-center justify-center gap-2 active:opacity-80"
        >
          <Link2 className="w-4 h-4" />
          {calendar.status === 'auth_expired' ? t('Yeniden bağlan', 'Reconnect') : t('Google Takvim’i bağla', 'Connect Google Calendar')}
        </button>
      </MobileCard>
    );
  }

  if (calendar.status === 'connecting' || (calendar.status === 'loading' && calendar.events.length === 0)) {
    return (
      <MobileCard className="p-4 flex items-center gap-2 text-[15px] text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('Takvim yükleniyor…', 'Loading calendar…')}
      </MobileCard>
    );
  }

  if (!next) {
    return (
      <MobileCard className="p-4 text-[15px] text-muted-foreground">
        {calendar.status === 'error' ? calendar.message : t('Yaklaşan toplantı yok.', 'No upcoming meetings.')}
      </MobileCard>
    );
  }

  const start = new Date(next.startISO);
  const minutes = Math.max(1, Math.round((new Date(next.endISO).getTime() - start.getTime()) / 60000));
  const isNow = start.getTime() <= Date.now();
  const isToday = start.toDateString() === new Date().toDateString();
  const where = next.conference?.label ?? next.location;
  const state = autoJoin.stateFor(next);

  const setJoin = async (join: boolean) => {
    setBusy(true);
    try {
      await autoJoin.setEventJoin(next, join);
    } catch (err) {
      onError((err as Error).message || t('Tercih kaydedilemedi.', 'Could not save your preference.'));
    } finally {
      setBusy(false);
    }
  };

  const botLine =
    state.kind === 'toggle'
      ? state.on
        ? { text: t('Bot otomatik katılacak', 'The bot will join automatically'), className: 'text-ok-text' }
        : { text: t('Bot katılmayacak', 'The bot won’t join'), className: 'text-muted-foreground' }
      : state.kind === 'status'
        ? { text: botStatusLabel({ status: state.status, meetingUrl: '' }).label, className: 'text-brand-text' }
        : null;

  return (
    <MobileCard className="p-4 space-y-3.5">
      <div className="flex items-start gap-3.5">
        <div className="w-[56px] shrink-0 rounded-[14px] bg-brand-soft text-brand-text flex flex-col items-center py-2">
          <span className="text-[16px] font-bold leading-[22px]">{isNow ? t('Şimdi', 'Now') : clock(next.startISO)}</span>
          <span className="text-[12px] font-semibold">{t(`${minutes} dk`, `${minutes} min`)}</span>
        </div>
        <div className="min-w-0 flex flex-col gap-[3px]">
          <span className="text-[17px] font-semibold leading-[23px] text-foreground line-clamp-2">{next.title}</span>
          <span className="text-[14px] text-muted-foreground truncate">
            {!isToday && `${start.toLocaleDateString(dateLocale(), { weekday: 'long', day: 'numeric', month: 'long' })} · `}
            {[where, next.attendeeCount && next.attendeeCount > 1 ? t(`${next.attendeeCount} katılımcı`, `${next.attendeeCount} attendees`) : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {botLine && (
            <span data-tour="next-meeting-bot" className={`mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold ${botLine.className}`}>
              <Bot className="w-4 h-4" />
              {botLine.text}
            </span>
          )}
        </div>
      </div>

      {(next.conference || state.kind === 'toggle') && (
        <div className="flex gap-2.5">
          {next.conference && (
            <button
              type="button"
              onClick={() => void window.zabit.openExternal(next.conference!.url).catch(() => {})}
              className={`flex-1 h-11 rounded-xl text-[15px] font-bold flex items-center justify-center gap-2 active:opacity-80 ${
                isNow ? 'bg-brand text-on-brand' : 'bg-secondary text-foreground'
              }`}
            >
              <Video className="w-4 h-4" />
              {t('Katıl', 'Join')}
            </button>
          )}
          {state.kind === 'toggle' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void setJoin(!state.on)}
              className={`flex-1 h-11 rounded-xl text-[15px] font-semibold flex items-center justify-center gap-2 active:opacity-80 disabled:opacity-50 ${
                state.on ? 'bg-secondary text-muted-foreground' : 'bg-brand text-on-brand font-bold'
              }`}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {state.on ? t('Katılmasın', 'Skip bot') : t('Bot gönder', 'Send bot')}
            </button>
          )}
        </div>
      )}
    </MobileCard>
  );
};
